import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Button, Alert, Spinner, PageHeader, Tabs, Tab, formatServiceArea, FilterChips } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import { areasService, ServiceArea, ForemanArea, ContractArea } from '../../../services/areasService';
import ServiceAreasTable from '../components/ServiceAreasTable';
import ForemanAreasTable from '../components/ForemanAreasTable';
import ContractAreasTable from '../components/ContractAreasTable';
import ServiceAreaModal from '../components/ServiceAreaModal';
import ForemanAreaModal from '../components/ForemanAreaModal';
import ContractAreaModal from '../components/ContractAreaModal';
import AreasFilters from '../components/AreasFilters';
import Toast from '../../../components/Toast';
import { useAreaList, type AreaListState } from '../hooks/useAreaList';
import {
  EMPTY_AREA_FILTERS,
  filterContractAreas,
  filterForemanAreas,
  filterServiceAreas,
  type AreaFilterValues,
} from '../utils/areaFilters';
import { confirmAction } from '@/components/ui';

const AREA_SECTIONS = ['service', 'foreman', 'contract'] as const;
type AreaSection = (typeof AREA_SECTIONS)[number];

/** sessionStorage page key for this page's remembered list state (V2-024). */
const LIST_KEY = 'areas';

/** Search: the area's name or code contains the text (case-insensitive). */
function matchesSearch(area: { name?: string | null; code?: string | null }, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return (area.name ?? '').toLowerCase().includes(q) || (area.code ?? '').toLowerCase().includes(q);
}

interface AreasPageProps {}

const AreasPage: React.FC<AreasPageProps> = () => {
  const { section: sectionParam } = useParams<{ section: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // Each list loads on its own and once (W-M17): the Service Areas tab no longer waits behind
  // the contract-area request, and switching tabs no longer re-sends all three requests.
  const serviceList = useAreaList<ServiceArea>(() => areasService.getServiceAreas(), 'service areas');
  const foremanList = useAreaList<ForemanArea>(() => areasService.getForemanAreas(), 'foreman areas');
  const contractList = useAreaList<ContractArea>(() => areasService.getAllContractAreas(), 'contract areas');
  const serviceAreas = useMemo(() => serviceList.data ?? [], [serviceList.data]);
  const foremanAreas = useMemo(() => foremanList.data ?? [], [foremanList.data]);
  const contractAreas = useMemo(() => contractList.data ?? [], [contractList.data]);

  // Modal states
  const [showServiceAreaModal, setShowServiceAreaModal] = useState(false);
  const [showForemanAreaModal, setShowForemanAreaModal] = useState(false);
  const [showContractAreaModal, setShowContractAreaModal] = useState(false);

  // Selected items for editing
  const [selectedServiceArea, setSelectedServiceArea] = useState<ServiceArea | null>(null);
  const [selectedForemanArea, setSelectedForemanArea] = useState<ForemanArea | null>(null);
  const [selectedContractArea, setSelectedContractArea] = useState<ContractArea | null>(null);

  // Filter state - shared by every tab, and set by the relationship links (W-M22). Kept for the
  // sign-in with the search (V2-024); the tab is in the URL.
  const [filters, setFilters] = usePersistedListState<AreaFilterValues>(LIST_KEY, 'filters', EMPTY_AREA_FILTERS);
  // The search filters the loaded lists, within the filters (SRCH-04), by name or code.
  const [search, setSearch] = usePersistedListState(LIST_KEY, 'search', '');
  const [searchInput, setSearchInput] = useState(search);
  /** A relationship link shows exactly the row it names: it replaces the filters and drops the search. */
  const showOnly = (next: AreaFilterValues) => {
    setFilters(next);
    setSearch('');
    setSearchInput('');
  };
  const runSearch = (value: string) => {
    setSearchInput(value.trim());
    setSearch(value.trim());
  };
  const clearAll = () => showOnly(EMPTY_AREA_FILTERS);

  // Toast state
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  /** After a save or delete: relationships can change, so refresh every list (each on its own). */
  const loadAllAreaLists = useCallback(async () => {
    await Promise.all([serviceList.reload(), foremanList.reload(), contractList.reload()]);
  }, [serviceList.reload, foremanList.reload, contractList.reload]);

  const section =
    sectionParam && AREA_SECTIONS.includes(sectionParam as AreaSection)
      ? (sectionParam as AreaSection)
      : null;

  // V3-030 / FLT-09: the selected tab is kept for the sign-in like the rest of the list state,
  // so Home -> Areas reopens Foreman Areas rather than Service Areas (read by AreasDefaultRedirect).
  const [, setSavedSection] = usePersistedListState<string>(LIST_KEY, 'tab', '');
  useEffect(() => {
    if (section) setSavedSection(section);
  }, [section, setSavedSection]);

  const activeTab: 'service-areas' | 'foreman-areas' | 'contract-areas' =
    section === 'foreman' ? 'foreman-areas' : section === 'contract' ? 'contract-areas' : 'service-areas';

  // Service Area handlers
  const handleAddServiceArea = () => {
    setSelectedServiceArea(null);
    setShowServiceAreaModal(true);
  };

  const handleEditServiceArea = (serviceArea: ServiceArea) => {
    setSelectedServiceArea(serviceArea);
    setShowServiceAreaModal(true);
  };

  const handleSaveServiceArea = async (serviceAreaData: Partial<ServiceArea>) => {
    try {
      if (selectedServiceArea) {
        await areasService.updateServiceArea(selectedServiceArea.id, serviceAreaData);
        setToastMessage('Service area updated successfully');
      } else {
        await areasService.createServiceArea(serviceAreaData as Omit<ServiceArea, 'id' | 'foremanAreas'>);
        setToastMessage('Service area created successfully');
      }
      setToastType('success');
      await loadAllAreaLists();
      setShowServiceAreaModal(false);
    } catch (err) {
      console.error('Error saving service area:', err);
      setToastMessage('Failed to save service area');
      setToastType('error');
    }
  };

  const handleDeleteServiceArea = async (id: number) => {
    if ((await confirmAction({ title: 'Delete Service Area?', body: 'This also affects every foreman area in it.', confirmLabel: 'Delete', destructive: true }))) {
      try {
        await areasService.deleteServiceArea(id);
        setToastMessage('Service area deleted successfully');
        setToastType('success');
        await loadAllAreaLists();
      } catch (err) {
        console.error('Error deleting service area:', err);
        setToastMessage('Failed to delete service area');
        setToastType('error');
      }
    }
  };

  // Foreman Area handlers
  const handleAddForemanArea = () => {
    setSelectedForemanArea(null);
    setShowForemanAreaModal(true);
  };

  const handleEditForemanArea = (foremanArea: ForemanArea) => {
    setSelectedForemanArea(foremanArea);
    setShowForemanAreaModal(true);
  };

  const handleSaveForemanArea = async (
    foremanAreaData: Partial<ForemanArea> & { contractAreaIds?: number[] }
  ) => {
    try {
      if (selectedForemanArea) {
        await areasService.updateForemanArea(selectedForemanArea.id, foremanAreaData);
        setToastMessage('Foreman area updated successfully');
      } else {
        await areasService.createForemanArea(
          foremanAreaData as Omit<ForemanArea, 'id' | 'serviceArea' | 'contractAreas'> & {
            contractAreaIds?: number[];
          }
        );
        setToastMessage('Foreman area created successfully');
      }
      setToastType('success');
      await loadAllAreaLists();
      setShowForemanAreaModal(false);
    } catch (err) {
      console.error('Error saving foreman area:', err);
      setToastMessage('Failed to save foreman area');
      setToastType('error');
    }
  };

  const handleDeleteForemanArea = async (id: number) => {
    if ((await confirmAction({ title: 'Delete Foreman Area?', body: 'This also affects every contract area in it.', confirmLabel: 'Delete', destructive: true }))) {
      try {
        await areasService.deleteForemanArea(id);
        setToastMessage('Foreman area deleted successfully');
        setToastType('success');
        await loadAllAreaLists();
      } catch (err) {
        console.error('Error deleting foreman area:', err);
        setToastMessage('Failed to delete foreman area');
        setToastType('error');
      }
    }
  };

  // Contract Area handlers
  const handleAddContractArea = () => {
    setSelectedContractArea(null);
    setShowContractAreaModal(true);
  };

  const handleEditContractArea = (contractArea: ContractArea) => {
    setSelectedContractArea(contractArea);
    setShowContractAreaModal(true);
  };

  const handleSaveContractArea = async (contractAreaData: Partial<Omit<ContractArea, 'foremanAreas' | 'assets'>> & { foremanAreaIds?: number[] }) => {
    try {
      if (selectedContractArea) {
        await areasService.updateContractArea(selectedContractArea.id, contractAreaData);
        setToastMessage('Contract area updated successfully');
      } else {
        // Ensure foremanAreaIds is provided for creation
        if (!contractAreaData.foremanAreaIds || contractAreaData.foremanAreaIds.length === 0) {
          throw new Error('At least one foreman area must be selected');
        }
        await areasService.createContractArea(contractAreaData as Omit<ContractArea, 'id' | 'foremanAreas' | 'assets'> & { foremanAreaIds: number[] });
        setToastMessage('Contract area created successfully');
      }
      setToastType('success');
      await loadAllAreaLists();
      setShowContractAreaModal(false);
    } catch (err) {
      console.error('Error saving contract area:', err);
      setToastMessage(err instanceof Error ? err.message : 'Failed to save contract area');
      setToastType('error');
    }
  };

  const handleDeleteContractArea = async (id: number) => {
    if ((await confirmAction({ title: 'Delete Contract Area?', body: 'This also affects every asset in it.', confirmLabel: 'Delete', destructive: true }))) {
      try {
        await areasService.deleteContractArea(id);
        setToastMessage('Contract area deleted successfully');
        setToastType('success');
        await loadAllAreaLists();
      } catch (err) {
        console.error('Error deleting contract area:', err);
        setToastMessage('Failed to delete contract area');
        setToastType('error');
      }
    }
  };

  // Filtered lists - tab counts are the filtered counts.
  const filteredServiceAreas = filterServiceAreas(serviceAreas, filters).filter((a) => matchesSearch(a, search));
  const filteredForemanAreas = filterForemanAreas(foremanAreas, filters).filter((a) => matchesSearch(a, search));
  const filteredContractAreas = filterContractAreas(contractAreas, filters).filter((a) => matchesSearch(a, search));

  // Relationship links (W-M22): a child count opens the child tab filtered to the row; a parent
  // name opens the parent tab filtered to that parent.
  const showForemanAreasOf = (sa: ServiceArea) => {
    showOnly({ serviceArea: sa.name, foremanArea: '' });
    navigate('/areas/foreman');
  };
  const showContractAreasOf = (fa: ForemanArea) => {
    showOnly({ serviceArea: '', foremanArea: fa.name });
    navigate('/areas/contract');
  };
  const showServiceArea = (name: string) => {
    showOnly({ serviceArea: name, foremanArea: '' });
    navigate('/areas/service');
  };
  const showForemanArea = (name: string) => {
    showOnly({ serviceArea: '', foremanArea: name });
    navigate('/areas/foreman');
  };

  const tabTitle = (label: string, list: AreaListState<unknown>, count: number) => (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
      {label}
      {list.loading && !list.data ? (
        <Spinner animation="border" size="sm" role="status" aria-label={`Loading ${label.toLowerCase()}`} />
      ) : list.error ? (
        <i className="bi bi-exclamation-circle tw-text-danger" aria-label="Could not load" />
      ) : (
        <span className="tw-tabular-nums">({count})</span>
      )}
    </span>
  );

  const renderTabBody = (list: AreaListState<unknown>, label: string, table: React.ReactNode) => {
    if (list.error) {
      return (
        <Alert variant={list.error.kind === 'forbidden' ? 'warning' : 'danger'} className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          <span className="tw-flex-1">{list.error.message}</span>
          {list.error.kind === 'error' && (
            <Button variant="outline-secondary" size="sm" onClick={() => void list.reload()} disabled={list.loading}>
              <i className="bi bi-arrow-clockwise tw-mr-1" aria-hidden="true" />
              Retry
            </Button>
          )}
        </Alert>
      );
    }
    if (!list.data) {
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-10 tw-text-content-muted" role="status">
          <Spinner animation="border" size="sm" />
          Loading {label}…
        </div>
      );
    }
    return table;
  };

  const filterChips = [
    filters.serviceArea && {
      key: 'serviceArea',
      label: formatServiceArea(filters.serviceArea, true) ?? filters.serviceArea,
      clear: () => setFilters((f) => ({ ...f, serviceArea: '' })),
    },
    filters.foremanArea && {
      key: 'foremanArea',
      label: filters.foremanArea,
      clear: () => setFilters((f) => ({ ...f, foremanArea: '' })),
    },
  ].filter(Boolean) as Array<{ key: string; label: string; clear: () => void }>;

  const filteredEmpty = (what: string) =>
    filterChips.length > 0 || search ? `No ${what} match the current filters.` : `No ${what} found.`;

  if (!section) {
    return <Navigate to="/areas" replace />;
  }
  if (section === 'service' && !hasPermission('area:service_view')) {
    return <Navigate to="/areas" replace />;
  }
  if (section === 'foreman' && !hasPermission('area:foreman_view')) {
    return <Navigate to="/areas" replace />;
  }
  if (section === 'contract' && !hasPermission('area:contract_view')) {
    return <Navigate to="/areas" replace />;
  }

  return (
    <div
      className="areas-page tw-p-4"
      style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
    >
      {/* One create action, resolved by the active tab. */}
      <PageHeader
        title="Areas"
        actions={[
          {
            key: 'add-service',
            label: 'Add Service Area',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !(activeTab === 'service-areas' && hasPermission('area:service_create')),
            onClick: handleAddServiceArea,
          },
          {
            key: 'add-foreman',
            label: 'Add Foreman Area',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !(activeTab === 'foreman-areas' && hasPermission('area:foreman_create')),
            onClick: handleAddForemanArea,
          },
          {
            key: 'add-contract',
            label: 'Add Contract Area',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !(activeTab === 'contract-areas' && hasPermission('area:contract_create')),
            onClick: handleAddContractArea,
          },
        ]}
      />

      <AreasFilters
        serviceAreas={serviceAreas}
        foremanAreas={foremanAreas}
        value={filters}
        onFilterChange={setFilters}
        appliedSearch={search}
        onClear={clearAll}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: runSearch,
          placeholder: 'Area name or code',
          label: 'Search areas',
          debounceMs: 300,
        }}
      />

      <FilterChips
        data-testid="areas-filter-chips"
        chips={filterChips.map((chip) => ({ key: chip.key, label: chip.label, onClear: chip.clear }))}
      />

      {/* The standard tab component (W-M17). Each tab renders as soon as its own list arrives. */}
      <Tabs
        activeKey={section}
        onSelect={(k) => k && navigate(`/areas/${k}`)}
        className="tw-mb-3"
      >
        {hasPermission('area:service_view') && (
          <Tab eventKey="service" title={tabTitle('Service Areas', serviceList, filteredServiceAreas.length)}>
            {renderTabBody(
              serviceList,
              'service areas',
              <ServiceAreasTable
                serviceAreas={filteredServiceAreas}
                onEdit={handleEditServiceArea}
                onDelete={handleDeleteServiceArea}
                canEdit={hasPermission('area:service_edit')}
                canDelete={hasPermission('area:service_delete')}
                onShowForemanAreas={hasPermission('area:foreman_view') ? showForemanAreasOf : undefined}
                emptyMessage={filteredEmpty('service areas')}
              />,
            )}
          </Tab>
        )}
        {hasPermission('area:foreman_view') && (
          <Tab eventKey="foreman" title={tabTitle('Foreman Areas', foremanList, filteredForemanAreas.length)}>
            {renderTabBody(
              foremanList,
              'foreman areas',
              <ForemanAreasTable
                foremanAreas={filteredForemanAreas}
                onEdit={handleEditForemanArea}
                onDelete={handleDeleteForemanArea}
                canEdit={hasPermission('area:foreman_edit')}
                canDelete={hasPermission('area:foreman_delete')}
                onShowContractAreas={hasPermission('area:contract_view') ? showContractAreasOf : undefined}
                onShowServiceArea={hasPermission('area:service_view') ? showServiceArea : undefined}
                emptyMessage={filteredEmpty('foreman areas')}
              />,
            )}
          </Tab>
        )}
        {hasPermission('area:contract_view') && (
          <Tab eventKey="contract" title={tabTitle('Contract Areas', contractList, filteredContractAreas.length)}>
            {renderTabBody(
              contractList,
              'contract areas',
              <ContractAreasTable
                contractAreas={filteredContractAreas}
                onEdit={handleEditContractArea}
                onDelete={handleDeleteContractArea}
                canEdit={hasPermission('area:contract_edit')}
                canDelete={hasPermission('area:contract_delete')}
                onShowForemanArea={hasPermission('area:foreman_view') ? showForemanArea : undefined}
                onShowServiceArea={hasPermission('area:service_view') ? showServiceArea : undefined}
                emptyMessage={filteredEmpty('contract areas')}
              />,
            )}
          </Tab>
        )}
      </Tabs>

      {/* Modals */}
      <ServiceAreaModal
        show={showServiceAreaModal}
        onHide={() => setShowServiceAreaModal(false)}
        serviceArea={selectedServiceArea}
        onSave={handleSaveServiceArea}
      />

      <ForemanAreaModal
        show={showForemanAreaModal}
        onHide={() => setShowForemanAreaModal(false)}
        foremanArea={selectedForemanArea}
        serviceAreas={serviceAreas}
        onSave={handleSaveForemanArea}
      />

      <ContractAreaModal
        show={showContractAreaModal}
        onHide={() => setShowContractAreaModal(false)}
        contractArea={selectedContractArea}
        foremanAreas={foremanAreas}
        onSave={handleSaveContractArea}
      />

      {/* Toast notifications */}
      <Toast
        message={toastMessage}
        type={toastType}
        show={!!toastMessage}
        onClose={() => setToastMessage('')}
      />
    </div>
  );
};

export default AreasPage;